// Runs between two geofenced areas, and where the time goes on them.
//
// The server pairs the crossings into journeys and judges each one against its corridor's own
// normal; this is the arithmetic a page does over whatever subset somebody has filtered to - a few
// hundred rows at most, which is browser work. The expensive part, scanning every crossing a site
// ever recorded, already happened on the scheduler.
//
// A corridor is an ordered pair. North to South is not the same run as South to North: one is loaded
// and one is empty, and averaging them describes neither.

export const corridorKey = (journey) => `${journey.fromAreaId}->${journey.toAreaId}`;

/** The middle run, which one breakdown cannot move. */
export const median = (values) => {
  if (!values.length) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const middle = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[middle] : (sorted[middle - 1] + sorted[middle]) / 2;
};

/** Every corridor that has been travelled, busiest first, for the picker. */
export const corridorsOf = (journeys) => {
  const seen = new Map();
  (journeys || []).forEach((j) => {
    const key = corridorKey(j);
    const held = seen.get(key) || {
      key, fromAreaId: j.fromAreaId, toAreaId: j.toAreaId, from: j.from, to: j.to, runs: 0,
    };
    held.runs += 1;
    seen.set(key, held);
  });
  return [...seen.values()].sort((a, b) => b.runs - a.runs);
};

/** The runs on one corridor inside a date range, newest first. */
export const runsOn = (journeys, key, from, to) => (journeys || [])
  .filter((j) => corridorKey(j) === key)
  .filter((j) => {
    const left = Date.parse(j.leftAt);
    if (Number.isNaN(left)) return false;
    if (from && left < Date.parse(from)) return false;
    if (to && left > Date.parse(to)) return false;
    return true;
  })
  .sort((a, b) => Date.parse(b.leftAt) - Date.parse(a.leftAt));

/**
 * Narrows the runs on a leg to the ones somebody asked about.
 *
 * Separate from runsOn, which answers "which runs belong to this leg in these dates" - a question
 * about the data. This answers "and which of those am I interested in", which is a question about
 * the person, and keeping them apart means the summary and the heatmap can be built from either.
 *
 * A late run is one that took longer than the usual time for its direction. Runs the server could
 * not judge - too few before them to know what usual is - are not late, because "we do not know" is
 * not "yes".
 */
export const narrow = (runs, { vehicleId = '', lateOnly = false, day = null, hour = null } = {}) => (runs || [])
  .filter((r) => (vehicleId === '' ? true : r.vehicleId === Number(vehicleId)))
  .filter((r) => (lateOnly ? (r.overMs ?? 0) > 0 : true))
  .filter((r) => {
    if (day == null && hour == null) return true;
    const at = new Date(r.leftAt);
    if (Number.isNaN(at.getTime())) return false;
    if (day != null && (at.getDay() + 6) % 7 !== day) return false;
    if (hour != null && at.getHours() !== hour) return false;
    return true;
  });

/**
 * Who was in the cab, from the trip the run belongs to.
 *
 * A crossing records a lorry rather than a person, which is right - a geofence sees a vehicle - but
 * every run carries the trip it was part of, and the trip knows its driver. Blank where there is no
 * trip against the run, rather than guessing from whoever usually drives that lorry.
 */
export const driverOf = (run, trips) => (trips || []).find((t) => t.id === run?.tripId)?.driver || '';

/**
 * A row per lorry on this leg: how many runs, what it usually takes, how many went over.
 *
 * Sorted by runs rather than by how slow it looks, because the lorry doing the work is the one whose
 * average means something. A lorry with two runs and a terrible figure is noise, and the count sits
 * beside the figure so a reader can see that without being told.
 */
export const byVehicle = (runs, vehicles, trips) => {
  const held = new Map();
  (runs || []).forEach((run) => {
    if (!held.has(run.vehicleId)) held.set(run.vehicleId, []);
    held.get(run.vehicleId).push(run);
  });
  return [...held.entries()]
    .map(([id, mine]) => ({
      id,
      name: vehicles?.find((v) => v.id === id)?.name || `Vehicle ${id}`,
      // The driver of the most recent run, which is the one somebody would ring about it.
      driver: driverOf([...mine].sort((a, b) => Date.parse(b.leftAt) - Date.parse(a.leftAt))[0], trips),
      runs: mine.length,
      usual: median(mine.map((r) => r.ms)),
      late: mine.filter((r) => (r.overMs ?? 0) > 0).length,
      worstMs: mine.reduce((worst, r) => Math.max(worst, r.overMs ?? 0), 0),
    }))
    .sort((a, b) => b.runs - a.runs || a.name.localeCompare(b.name));
};

/** The vehicles that have actually run a leg, so the chooser offers nothing that cannot match. */
export const vehiclesOn = (runs, vehicles) => [...new Set((runs || []).map((r) => r.vehicleId))]
  .map((id) => ({ id, name: vehicles?.find((v) => v.id === id)?.name || `Vehicle ${id}` }))
  .sort((a, b) => a.name.localeCompare(b.name));

/**
 * What this corridor looks like over the chosen runs.
 *
 * The usual figure is the median of the runs on screen rather than the server's stored normal, so
 * the headline and the rows a reader can count always agree. Where there are too few runs to mean
 * anything it says so instead of dividing by a handful.
 */
export const summarise = (runs) => {
  const times = runs.map((r) => r.ms).filter((m) => typeof m === 'number' && m >= 0);
  if (!times.length) return null;
  const usual = median(times);
  const late = runs.filter((r) => typeof r.overMs === 'number' && r.overMs > 0);
  return {
    runs: times.length,
    usual,
    quickest: Math.min(...times),
    slowest: Math.max(...times),
    // Enough to be worth a word. Fewer and the spread is just noise.
    trustworthy: times.length >= 3,
    lateRuns: late.length,
    worstOver: late.length ? Math.max(...late.map((r) => r.overMs)) : null,
  };
};

export const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

/**
 * When this run is slow: a cell per hour of the day, per day of the week.
 *
 * The question a haulier actually asks about a shuttle is not "what is the average" but "when does
 * it go wrong" - and the answer is usually a time of day, because that is when the traffic is, or
 * when the shift changes, or when one dock is on its break.
 *
 * Each cell carries the median of the runs that started in it and how many there were. A cell with
 * one run in it is not evidence, so the count travels with the figure and the page can say so rather
 * than colouring it as though it were.
 */
export const heatmap = (runs) => {
  const cells = new Map();
  runs.forEach((run) => {
    const at = new Date(run.leftAt);
    if (Number.isNaN(at.getTime())) return;
    // Monday first: a working week reads Monday to Sunday, not Sunday to Saturday.
    const day = (at.getDay() + 6) % 7;
    const hour = at.getHours();
    const key = `${day}:${hour}`;
    if (!cells.has(key)) cells.set(key, []);
    cells.get(key).push(run.ms);
  });
  const out = [];
  cells.forEach((times, key) => {
    const [day, hour] = key.split(':').map(Number);
    out.push({ day, hour, runs: times.length, ms: median(times) });
  });
  return out;
};

/**
 * The hours the chart covers: the first with runs through to the last, without gaps.
 *
 * Not every hour of the day, because an idle night is sixteen blank columns nobody needs. But not
 * only the hours that have runs either, which was the first attempt: that draws 08 beside 10, and
 * would draw 08 beside 17, so two neighbouring squares are sometimes an hour apart and sometimes
 * nine. On a chart that exists to answer "when", an axis where distance does not mean time is worse
 * than a few empty columns in the middle - those at least say "nothing set off then", truthfully.
 */
export const hoursWithRuns = (cells) => {
  if (!cells.length) return [];
  const hours = cells.map((c) => c.hour);
  const first = Math.min(...hours);
  const last = Math.max(...hours);
  return Array.from({ length: last - first + 1 }, (_, i) => first + i);
};

/**
 * The days that have runs, in week order, so the grid is the shape of the data.
 *
 * Drawing all seven meant a weekday-only corridor carried two rows of nothing for ever, and a fleet
 * whose departures cluster showed a grid that was almost entirely empty squares. A row that is
 * always blank teaches a reader to ignore the chart.
 */
export const daysWithRuns = (cells) => [...new Set(cells.map((c) => c.day))].sort((a, b) => a - b);
